import { linearGradientDef } from '@nivo/core';
import { Datum, ResponsiveLine } from '@nivo/line';
import { FormikHelpers, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { object, string } from 'yup';

import Cabecalho from 'Component/Admin/Cabecalho';
import Botao from 'Component/Botao';
import Form, { Campo } from 'Component/Form';
import Icone from 'Component/Icone';
import { HowManySubscriptionsInterface } from 'Interface/Assinaturas';
import { TotalCadastradoCursos } from 'Interface/Cursos';
import Parametros from 'Interface/Requisicao';
import { ValorTotalTransacoes } from 'Interface/Transacoes';
import { TotalCadastradoUsuarios } from 'Interface/Usuarios';
import {
  useGetHowManySubscriptionsQuery,
  useRecuperarQuantidadePorDataTransacoesQuery,
  useRecuperarTotalCadastradoCursosQuery,
  useRecuperarTotalCadastradoPorDataUsuariosQuery,
  useRecuperarTotalCadastradoUsuariosQuery,
  useRecuperarValorTotalPorDataTransacoesQuery,
  useRecuperarValorTotalTransacoesQuery,
} from 'Service';
import { mapearParaGraficos } from 'View/functions';

const regras = object({
  busca: string().optional().min(3, 'Deve ter mais que 3 letras'),
});

const Default = (): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filtro = {
    options: {
      data_e_tempo: {
        criado:
          searchParams.get('data_inicio') && searchParams.get('data_termino')
            ? [
                searchParams.get('data_inicio'),
                searchParams.get('data_termino'),
              ]
            : [],
      },
    },
    scope: false,
  } as Parametros;
  const config = {
    skip: !searchParams.get('data_inicio') || !searchParams.get('data_termino'),
  };

  const {
    data: {
      usuarios: [totalDeUsuariosCadastrados] = [{} as TotalCadastradoUsuarios],
    } = {},
    refetch: buscarTotalDeUsuariosCadastrados,
  } = useRecuperarTotalCadastradoUsuariosQuery(filtro, config);
  const {
    data: {
      transacoes: [valorTotalDeTransacoes] = [{} as ValorTotalTransacoes],
    } = {},
    refetch: buscarValorTotalDeTransacoes,
  } = useRecuperarValorTotalTransacoesQuery(filtro, config);
  const {
    data: {
      cursos: [totalDeCursosCadastrados] = [{} as TotalCadastradoCursos],
    } = {},
    refetch: buscarTotalDeCursosCadastrados,
  } = useRecuperarTotalCadastradoCursosQuery(filtro, config);
  const {
    data: {
      assinaturas: [howManySubscriptions] = [
        {} as HowManySubscriptionsInterface,
      ],
    } = {},
    refetch: refetchHowManySubscriptions,
  } = useGetHowManySubscriptionsQuery({
    scope: false,
  });
  const {
    data: { usuarios: totalDeUsuariosCadastradosPorData } = {},
    refetch: buscarTotalDeUsuariosCadastradosPorData,
  } = useRecuperarTotalCadastradoPorDataUsuariosQuery(filtro, config);
  const {
    data: { transacoes: quantidadeDeTransacoesPorData } = {},
    refetch: buscarQuantidadeDeTransacoesPorData,
  } = useRecuperarQuantidadePorDataTransacoesQuery(filtro, config);
  const {
    data: { transacoes: valorTotalDeTransacoesPorData } = {},
    refetch: buscarValorTotalDeTransacoesPorData,
  } = useRecuperarValorTotalPorDataTransacoesQuery(filtro, config);

  useEffect(() => {
    if (!searchParams.get('data_inicio') && !searchParams.get('data_termino')) {
      searchParams.set(
        'data_inicio',
        DateTime.now().toFormat('yyyy-LL-\'01T00:00\'')
      );
      searchParams.set(
        'data_termino',
        DateTime.now().toFormat('yyyy-LL-dd\'T23:59\'')
      );

      setSearchParams(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rebuscar = (): void => {
    buscarTotalDeUsuariosCadastrados();
    buscarValorTotalDeTransacoes();
    buscarTotalDeCursosCadastrados();
    refetchHowManySubscriptions();
    buscarTotalDeUsuariosCadastradosPorData();
    buscarQuantidadeDeTransacoesPorData();
    buscarValorTotalDeTransacoesPorData();
  };

  const filtrar = (
    dados: { data_inicio: string; data_termino: string },
    form: FormikHelpers<FormikValues>
  ) => {
    if (dados.data_inicio) {
      searchParams.set('data_inicio', dados.data_inicio);
    } else {
      searchParams.delete('data_inicio');
    }
    if (dados.data_termino) {
      searchParams.set('data_termino', dados.data_termino);
    } else {
      searchParams.delete('data_termino');
    }

    setSearchParams(searchParams);

    form.setSubmitting(false);
  };

  return (
    <div className="animate__animated animate__fadeIn">
      <Cabecalho
        titulo="Dashboard"
        descricao="Veja um resumo geral das estatísticas"
      />
      <Form emLinha onSubmit={filtrar} regras={regras}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <Botao
              icone="arrow-clockwise"
              className="me-2"
              onClick={rebuscar}
            />
          </div>
          <div className="col-md-6 mb-3">
            <div className="hstack gap-3">
              <Campo
                name="data_inicio"
                rotulo="Data inicial"
                type="datetime-local"
                forcarValor={searchParams.get('data_inicio')}
                desabilitarAoForcar={false}
              />
              <Campo
                name="data_termino"
                rotulo="Data inicial"
                type="datetime-local"
                forcarValor={searchParams.get('data_termino')}
                desabilitarAoForcar={false}
              />
              <Botao type="submit" icone="filter" />
            </div>
          </div>
        </div>
      </Form>
      <div className="row">
        <div className="col-md-12">
          <div className="row">
            <div className="col-6 col-lg-6 col-xxl-3 mb-3">
              <div className="bg-white border border-opacity-50 border-info rounded shadow-sm w-100 p-3">
                <div className="text-end">
                  <div className="fs-1 d-flex justify-content-between mb-3">
                    <Icone
                      nome="person-plus-fill"
                      className="display-1 text-info"
                    />
                    <span>{totalDeUsuariosCadastrados?.total || 0}</span>
                  </div>
                  Novos cadastros
                </div>
              </div>
            </div>
            <div className="col-6 col-lg-6 col-xxl-3 mb-3">
              <div className="bg-white border border-opacity-50 border-success rounded shadow-sm w-100 p-3">
                <div className="text-end">
                  <div className="fs-1 d-flex justify-content-between mb-3">
                    <Icone nome="book" className="display-1 text-success" />
                    <span>{totalDeCursosCadastrados?.total || 0}</span>
                  </div>
                  Novos cursos
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-xxl-3 mb-3">
              <div className="bg-white border border-opacity-50 border-danger rounded shadow-sm w-100 p-3">
                <div className="text-end">
                  <div className="fs-1 d-flex justify-content-between mb-3">
                    <Icone
                      nome="currency-dollar"
                      className="display-1 text-danger"
                    />
                    <span>{(valorTotalDeTransacoes?.total || 0).moeda()}</span>
                  </div>
                  Total transacionado
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-xxl-3 mb-3">
              <Link
                to="/admin/usuarios?assinatura=1"
                className="text-decoration-none text-body"
              >
                <div className="bg-white border border-opacity-50 border-secondary rounded shadow-sm w-100 p-3">
                  <div className="text-end">
                    <div className="fs-1 d-flex justify-content-between">
                      <Icone
                        nome="credit-card-2-front"
                        className="display-1 text-secondary"
                      />
                      <span>{howManySubscriptions?.total || 0}</span>
                    </div>
                    <div className="mt-n1">
                      Total de assinantes
                      <small className="d-block text-muted">
                        Não segue o filtro especificado
                      </small>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <div className="bg-white rounded shadow-sm w-100 p-3">
            <div className="hf-400">
              <ResponsiveLine
                data={[
                  {
                    id: 'cadastros',
                    data: (totalDeUsuariosCadastradosPorData
                      ? mapearParaGraficos(
                          totalDeUsuariosCadastradosPorData,
                          'data',
                          'total'
                        )
                      : []) as unknown as Datum[],
                  },
                ]}
                enableArea={true}
                colors={{ scheme: 'category10' }}
                defs={[
                  linearGradientDef('gradientA', [
                    { offset: 0, color: 'inherit' },
                    { offset: 100, color: 'inherit', opacity: 0 },
                  ]),
                ]}
                fill={[{ match: '*', id: 'gradientA' }]}
                margin={{ top: 30, right: 10, bottom: 50, left: 60 }}
                curve="monotoneX"
                xScale={{
                  type: 'time',
                  format: '%Y-%m-%d',
                  useUTC: false,
                  precision: 'day',
                }}
                axisBottom={{
                  format: '%d/%b',
                  tickValues: 'every 1 days',
                  legend: 'dias corridos',
                  legendOffset: -12,
                }}
                xFormat="time:%Y-%m-%d"
                enablePointLabel={true}
                pointSize={16}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                enableSlices="x"
              />
            </div>
            <div className="mt-3 d-flex align-items-center justify-content-center">
              <Icone nome="person-plus-fill" className="fs-1 me-3" />
              <p className="fs-6 mb-0">
                Usuários cadastrados por dia
                <br />
                <small className="text-muted">Valor maior é melhor</small>
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <div className="bg-white rounded shadow-sm w-100 p-3">
            <div className="hf-400">
              <ResponsiveLine
                data={[
                  {
                    id: 'quantidade',
                    data: (quantidadeDeTransacoesPorData
                      ? mapearParaGraficos(
                          quantidadeDeTransacoesPorData,
                          'data',
                          'total'
                        )
                      : []) as unknown as Datum[],
                  },
                ]}
                enableArea={true}
                colors={{ scheme: 'category10' }}
                defs={[
                  linearGradientDef('gradientA', [
                    { offset: 0, color: 'inherit' },
                    { offset: 100, color: 'inherit', opacity: 0 },
                  ]),
                ]}
                fill={[{ match: '*', id: 'gradientA' }]}
                margin={{ top: 30, right: 10, bottom: 50, left: 60 }}
                curve="monotoneX"
                xScale={{
                  type: 'time',
                  format: '%Y-%m-%d',
                  useUTC: false,
                  precision: 'day',
                }}
                axisBottom={{
                  format: '%d/%b',
                  tickValues: 'every 1 days',
                  legend: 'dias corridos',
                  legendOffset: -12,
                }}
                xFormat="time:%Y-%m-%d"
                enablePointLabel={true}
                pointSize={16}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                enableSlices="x"
              />
            </div>
            <div className="mt-3 d-flex align-items-center justify-content-center">
              <Icone nome="arrow-up-square-fill" className="fs-1 me-3" />
              <p className="fs-6 mb-0">
                Quantidade e valor total das transações por dia
                <br />
                <small className="text-muted">Valor maior é melhor</small>
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-12 mb-3">
          <div className="bg-white rounded shadow-sm w-100 p-3">
            <div className="hf-400">
              <ResponsiveLine
                data={[
                  {
                    id: 'valor',
                    data: (valorTotalDeTransacoesPorData
                      ? mapearParaGraficos(
                          valorTotalDeTransacoesPorData,
                          'data',
                          'total'
                        )
                      : []) as unknown as Datum[],
                  },
                ]}
                enableArea={true}
                colors={{ scheme: 'category10' }}
                defs={[
                  linearGradientDef('gradientA', [
                    { offset: 0, color: 'inherit' },
                    { offset: 100, color: 'inherit', opacity: 0 },
                  ]),
                ]}
                fill={[{ match: '*', id: 'gradientA' }]}
                margin={{ top: 30, right: 10, bottom: 50, left: 60 }}
                curve="monotoneX"
                xScale={{
                  type: 'time',
                  format: '%Y-%m-%d',
                  useUTC: false,
                  precision: 'day',
                }}
                axisBottom={{
                  format: '%d/%b',
                  tickValues: 'every 1 days',
                  legend: 'dias corridos',
                  legendOffset: -12,
                }}
                xFormat="time:%Y-%m-%d"
                enablePointLabel={true}
                pointSize={16}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                enableSlices="x"
              />
            </div>
            <div className="mt-3 d-flex align-items-center justify-content-center">
              <Icone nome="cash-coin" className="fs-1 me-3" />
              <p className="fs-6 mb-0">
                Quantidade e valor total das transações por dia
                <br />
                <small className="text-muted">Valor maior é melhor</small>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Default;
